import { IconCopy } from '@tabler/icons-react';
import { Box, Group, Stack, Text } from '@zorro/web/design-system';
import { showSuccessNotification } from '@zorro/web/ui-utils';
import { Children, ReactElement, ReactNode, isValidElement } from 'react';

import { CopyToClipboard } from '../CopyToClipboard';

type ChildrenProps = {
  children: ReactNode;
};

export const CopyableTooltipContent = ({ children }: ChildrenProps) => (
  <Stack gap="xxs">{children}</Stack>
);

export const CopyableTooltipLabel = ({ children }: ChildrenProps) => (
  <Text size="sm" c="var(--color-text-default-secondary)">
    {children}
  </Text>
);

export const CopyableTooltipValue = ({ children }: ChildrenProps) => (
  <Text size="sm">{children}</Text>
);

export const CopyableTooltipItem = ({ children }: ChildrenProps) => {
  const childArray = Children.toArray(children);
  const isLabel = (child: ReactNode) =>
    isValidElement(child) && child.type === CopyableTooltipLabel;

  return (
    <Stack gap="xxs">
      {childArray.filter(isLabel)}
      <Group gap="xxs">{childArray.filter((child) => !isLabel(child))}</Group>
    </Stack>
  );
};

export const CopyableTooltipTarget = ({
  children,
}: {
  children: ReactNode;
}) => <Box>{children}</Box>;

type CopyProps = {
  text: string;
  successMessage?: string;
  onCopy?: () => void;
  children?: ReactElement;
};

export const CopyableTooltipCopy = ({
  text,
  successMessage,
  onCopy,
  children,
}: CopyProps) => (
  <CopyToClipboard
    text={text}
    onCopy={(_text, result) => {
      if (result === 'failed') {
        return;
      }
      onCopy?.();
      if (successMessage) {
        showSuccessNotification({ message: successMessage });
      }
    }}
  >
    {children ?? (
      <IconCopy
        size={16}
        color="var(--color-icon-stroke-system-secondary)"
        cursor="pointer"
      />
    )}
  </CopyToClipboard>
);
